import { EllipsisOutlined, InfoCircleOutlined, PlusCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { PageHeader } from '@ant-design/pro-components';
import { useQuery } from '@apollo/client/react';
import { Markable } from '@medcom/shared-ui/components/Markable.tsx';
import { monoFont, tabularNums } from '@medcom/shared-ui/styles/globalCss.ts';
import { MIN_TOKEN_LENGTH_PG_TRGM, shortTokensDropped, tokenize } from '@medcom/shared-ui/utils/tokenSearch.ts';
import { Alert, Button, DatePicker, Dropdown, Empty, Input, Modal, Space, Table, Tooltip, Typography } from 'antd';
import type { ItemType } from 'antd/es/menu/interface';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import type React from 'react';
import { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { Link, useNavigate } from 'react-router';
import { useDebouncedCallback } from 'use-debounce';
import { MainContent } from '../components/MainContent';
import type { PatientDataInput, PatientSearchFilter, PatientSearchQuery } from '../graphql/generated/graphql.ts';
import { useAppStore } from '../hooks/store/useAppStore';
import { tableActionCell, tableHoverPointer } from '../styles/globalCss';
import { API_DATE_FORMAT, SHORT_DATE_FORMAT, SHORT_DATETIME_FORMAT } from '../utils/dateFormatUtils';
import { patientAddress, patientFullName } from '../utils/patient';
import { hasSomeRole, Role } from '../utils/user';
import { Forms } from './forms/Forms.tsx';
import { PATIENT_SEARCH_QUERY } from './PatientSearch.gql';
import { usePatientMenuItems } from './patientMenuItems.tsx';

export type Request = NonNullable<PatientSearchQuery['patientSearch']>['entries'][number];

export const PatientSearch: React.FC<{ showNewButton: boolean }> = ({ showNewButton }) => {
  const navigate = useNavigate();
  const auth = useAuth();
  const setPatientData = useAppStore(state => state.setPatientData);
  const [filter, setFilter] = useState<PatientSearchFilter>({
    search: '',
    birthday: null,
  });
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearch = useDebouncedCallback(value => setFilter({ ...filter, search: value }), 300);
  const validBackendTokens = tokenize(filter.search, { minTokenLength: MIN_TOKEN_LENGTH_PG_TRGM });
  const droppedTokens = shortTokensDropped(filter.search);
  const anyFilterActive = validBackendTokens.length > 0 || filter.birthday;
  const [formsPatientData, setFormsPatientData] = useState<PatientDataInput | null>(null);
  const patientMenuItems = usePatientMenuItems(setFormsPatientData);

  const newRequestAllowed = hasSomeRole(
    [Role.ROLE_LR_MEDCOM, Role.ROLE_LR_USER, Role.ROLE_LR_LAB_ADMIN, Role.ROLE_LR_FRONT_DESK],
    auth.user
  );

  const { data, loading, error } = useQuery(PATIENT_SEARCH_QUERY, {
    variables: {
      // Send only the tokens that meet the backend's pg_trgm minimum length;
      // short tokens are surfaced to the user via the droppedTokens hint instead.
      filter: { ...filter, search: validBackendTokens.join(' ') },
    },
    skip: !anyFilterActive,
  });

  const columns: ColumnsType<Request> = [
    {
      title: 'Nachname',
      dataIndex: ['patientData', 'lastName'],
      key: 'patientData.lastName',
      sorter: (a, b) => a.patientData.lastName.localeCompare(b.patientData.lastName),
      width: 110,
      ellipsis: true,
      render: (_, record) => (
        <Typography.Text style={{ fontWeight: 500 }}>
          <Markable tokens={validBackendTokens}>{record.patientData.lastName}</Markable>
        </Typography.Text>
      ),
    },
    {
      title: 'Vorname',
      dataIndex: ['patientData', 'firstName'],
      key: 'patientData.firstName',
      sorter: (a, b) => a.patientData.firstName.localeCompare(b.patientData.firstName),
      width: 100,
      ellipsis: true,
      render: (_, record) => <Markable tokens={validBackendTokens}>{record.patientData.firstName}</Markable>,
    },
    {
      title: 'SVNR',
      dataIndex: ['patientData', 'svnr'],
      key: 'patientData.svnr',
      sorter: (a, b) => a.patientData.svnr.localeCompare(b.patientData.svnr),
      width: 70,
      ellipsis: true,
      render: value => (
        <span className={monoFont}>
          <Markable tokens={validBackendTokens}>{value}</Markable>
        </span>
      ),
    },
    {
      title: 'Geburtstag',
      dataIndex: ['patientData', 'birthday'],
      key: 'patientData.birthday',
      sorter: (a, b) => a.patientData.birthday.localeCompare(b.patientData.birthday),
      width: 120,
      ellipsis: true,
      render: value => <span className={tabularNums}>{dayjs(value).format(SHORT_DATE_FORMAT)}</span>,
    },
    {
      title: 'Kasse',
      dataIndex: 'insuranceShortName',
      key: 'insuranceShortName',
      sorter: (a, b) => a.insuranceShortName.localeCompare(b.insuranceShortName),
      width: 100,
      ellipsis: true,
    },
    {
      title: 'Adresse',
      dataIndex: ['patientData', 'zip'],
      key: 'patientData.zip',
      sorter: (a, b) => patientAddress(a.patientData).localeCompare(patientAddress(b.patientData)),
      width: 100,
      ellipsis: true,
      render: (_, record) => patientAddress(record.patientData),
    },
    {
      title: 'Stand vom',
      dataIndex: 'createdAt',
      key: 'createdAt',
      defaultSortOrder: 'descend',
      width: 100,
      ellipsis: true,
      sorter: (a, b) => a.createdAt.localeCompare(b.createdAt),
      render: value => <span className={tabularNums}>{dayjs(value).format(SHORT_DATETIME_FORMAT)}</span>,
    },
    {
      title: '',
      key: 'actions',
      fixed: 'right',
      align: 'right',
      ellipsis: true,
      width: '100px',
      className: tableActionCell,
      render: (_, record) => {
        const items: ItemType[] = [];
        items.push(...patientMenuItems(record.patientData));

        return (
          <Dropdown menu={{ items: items }} trigger={['click']} placement="bottomRight">
            <Button
              icon={<EllipsisOutlined style={{ fontSize: '20px', verticalAlign: 'middle' }} />}
              type="text"
              onClick={e => e.stopPropagation()}
            />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <MainContent>
      <Space orientation="vertical" style={{ width: '100%' }}>
        <PageHeader
          title="Patientensuche"
          style={{ padding: 0, paddingBottom: 'inherit' }}
          extra={
            <Space key="searchbar" orientation="horizontal">
              <Input
                autoFocus
                allowClear
                placeholder="Suche - mind. 3 Zeichen eingeben"
                value={searchValue ?? ''}
                onChange={e => {
                  setSearchValue(e.target.value);
                  debouncedSearch(e.target.value);
                }}
                prefix={<SearchOutlined />}
                suffix={
                  <Tooltip title="Suche nach Vorname, Nachname oder SVNR">
                    <InfoCircleOutlined />
                  </Tooltip>
                }
                style={{ width: '350px' }}
              />
              <DatePicker
                format={SHORT_DATE_FORMAT}
                placeholder="Geburtstag"
                allowClear
                onChange={date =>
                  setFilter({
                    ...filter,
                    birthday: date ? date.format(API_DATE_FORMAT) : null,
                  })
                }
                disabledDate={current => current && (current.isAfter(dayjs()) || current.isBefore(dayjs().subtract(120, 'years')))}
              />
              {showNewButton && (
                <Link to="/anforderung/patient-erfassung">
                  <Button icon={<PlusCircleOutlined />} type="primary">
                    Patient erfassen
                  </Button>
                </Link>
              )}
            </Space>
          }
        />
        {error && <Alert showIcon type="error" title="Es ist ein Fehler bei der Suchabfrage aufgetreten" />}
        {droppedTokens.length > 0 && (
          <Alert
            showIcon
            type="warning"
            title={`Folgende Suchbegriffe sind zu kurz und werden ignoriert: ${droppedTokens.join(', ')} (mindestens ${MIN_TOKEN_LENGTH_PG_TRGM} Zeichen pro Wort)`}
          />
        )}
        {data?.patientSearch?.exact === false && (
          <Alert
            showIcon
            type="info"
            title="Das Suchergebnis beinhaltet nicht alle möglichen Patienten. Bitte schränken Sie die Suche weiter ein (z.B. mit Angabe der SVNR), um exakte Ergbnisse zu erhalten."
          />
        )}
        <Table<Request>
          scroll={{ x: 'max-content' }}
          sticky
          showSorterTooltip={false}
          rowKey={record => record.id}
          size="middle"
          dataSource={data?.patientSearch?.entries ?? []}
          pagination={false}
          loading={loading}
          columns={columns}
          onRow={record => ({
            onClick: () => {
              if (!newRequestAllowed) {
                return;
              }
              setPatientData(record.patientData);
              navigate('/anforderung/patient-erfassung');
            },
          })}
          rowClassName={newRequestAllowed ? tableHoverPointer : undefined}
          locale={{
            emptyText: (
              <Empty
                description={anyFilterActive ? 'Keine Patienten gefunden' : 'Bitte geben Sie Suchkriterien an'}
                image={Empty.PRESENTED_IMAGE_DEFAULT}
              />
            ),
          }}
        />
      </Space>
      <Modal
        title={`Formulare für ${formsPatientData && patientFullName(formsPatientData)}`}
        width={768}
        open={!!formsPatientData}
        footer={null}
        onCancel={() => setFormsPatientData(null)}
        destroyOnHidden
      >
        {formsPatientData && <Forms patientData={formsPatientData} />}
      </Modal>
    </MainContent>
  );
};
